import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Plus, ChevronLeft, ChevronRight, ChevronDown, X, Linkedin, CheckCircle, Clock } from 'lucide-react';
import { apiService, contactsAPI } from '../services/api';
import useEventMeetings from '../hooks/useEventMeetings';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import MeetingRequestDrawer from './MeetingRequestDrawer';
import MeetingDetailsDrawer from './MeetingDetailsDrawer';
import './EventHome.css';
import './EventAttendees.css';

const EventAttendees = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [attendees, setAttendees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [filterView, setFilterView] = useState('main');
    const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
    const [selectedAffinityGroups, setSelectedAffinityGroups] = useState([]);
    const [userEntityName, setUserEntityName] = useState(null);
    const [hasAppliedDefaultFilter, setHasAppliedDefaultFilter] = useState(false);
    
    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const filterRef = useRef(null);
    
    // Meeting Request Drawer state
    const [meetingRequestDrawerOpen, setMeetingRequestDrawerOpen] = useState(false);
    const [meetingRequestContact, setMeetingRequestContact] = useState(null);
    
    // Meeting Details Drawer state
    const [meetingDetailsDrawerOpen, setMeetingDetailsDrawerOpen] = useState(false);
    const [selectedMeetingId, setSelectedMeetingId] = useState(null);
    const [selectedMeetingCompanyName, setSelectedMeetingCompanyName] = useState(null);
    
    // Affinity groups expansion state
    const [expandedAffinityGroups, setExpandedAffinityGroups] = useState({});

    // Event meetings hook (SQL Server) - reusable across pages
    const {
        refresh: refreshMeetings,
        getMeetingStatusForCompany
    } = useEventMeetings(event?.EventId, event?.ic_event_id, { autoFetch: false });

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId]);

    // Fetch meetings when event is loaded (in background)
    useEffect(() => {
        if (event?.EventId && event?.ic_event_id) {
            refreshMeetings();
        }
    }, [event?.EventId, event?.ic_event_id, refreshMeetings]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterDropdownOpen(false);
            }
        };

        if (filterDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filterDropdownOpen]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // First get the event
            const eventResponse = await apiService.getEventByNameId(eventNameId);
            setEvent(eventResponse.data);

            // Then fetch attendees with the event ID
            if (eventResponse.data?.EventId) {
                const contactsResponse = await apiService.getAvailableContacts(eventResponse.data.EventId);
                setAttendees(contactsResponse.data.contacts || []);
                
                // Get user's entity type from the response
                if (contactsResponse.data.user_entity_name) {
                    setUserEntityName(contactsResponse.data.user_entity_name);
                }
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load attendees');
        } finally {
            setLoading(false);
        }
    };

    // Get distinct entity types for filtering
    const distinctEntityTypes = useMemo(() => {
        if (!attendees.length) return [];
        const types = new Set(attendees.map(a => a.entity_name).filter(Boolean));
        return Array.from(types).sort();
    }, [attendees]);

    // Auto-apply default filter: If user is a Philanthropist, select all entity types (including Philanthropist)
    useEffect(() => {
        if (!hasAppliedDefaultFilter && userEntityName === 'Philanthropist' && distinctEntityTypes.length > 0) {
            setSelectedEntityTypes([...distinctEntityTypes]);
            setHasAppliedDefaultFilter(true);
        }
    }, [userEntityName, distinctEntityTypes, hasAppliedDefaultFilter]);

    // Get distinct affinity groups for filtering (only from Philanthropists)
    const distinctAffinityGroups = useMemo(() => {
        if (!attendees.length) return [];
        const groups = new Set();
        attendees.forEach(a => {
            // Only collect affinity groups from Philanthropists
            if (a.entity_name === 'Philanthropist' && a.affinity_groups && Array.isArray(a.affinity_groups)) {
                a.affinity_groups.forEach(g => groups.add(g));
            }
        });
        return Array.from(groups).sort();
    }, [attendees]);

    // Filter attendees based on search and filters
    const filteredAttendees = useMemo(() => {
        return attendees.filter(attendee => {
            // Search filter
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const fullName = `${attendee.first_name || ''} ${attendee.last_name || ''}`.toLowerCase();
                const company = (attendee.company_name || '').toLowerCase();
                const title = (attendee.job_title || '').toLowerCase();

                if (!fullName.includes(query) && !company.includes(query) && !title.includes(query)) {
                    return false;
                }
            }

            // Entity type filter
            if (selectedEntityTypes.length > 0) {
                if (!selectedEntityTypes.includes(attendee.entity_name)) {
                    return false;
                }
            }

            // Affinity groups filter (only applies to Philanthropists)
            if (selectedAffinityGroups.length > 0) {
                // Only Philanthropists can match affinity group filters
                if (attendee.entity_name !== 'Philanthropist') {
                    return false;
                }
                if (!attendee.affinity_groups || !Array.isArray(attendee.affinity_groups)) {
                    return false;
                }
                const hasMatchingGroup = selectedAffinityGroups.some(g => attendee.affinity_groups.includes(g));
                if (!hasMatchingGroup) {
                    return false;
                }
            }

            return true;
        });
    }, [attendees, searchQuery, selectedEntityTypes, selectedAffinityGroups]);

    const toggleFilter = (filterType, value) => {
        if (filterType === 'entity') {
            setSelectedEntityTypes(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        } else if (filterType === 'affinity') {
            setSelectedAffinityGroups(prev =>
                prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
            );
        }
    };

    const clearFilters = () => {
        setSelectedEntityTypes([]);
        setSelectedAffinityGroups([]);
    };

    const activeFilterCount = selectedEntityTypes.length + selectedAffinityGroups.length;

    const handleAttendeeClick = async (attendee) => {
        setSelectedAttendee(attendee);
        setDrawerOpen(true);
        setProfileLoading(true);
        
        try {
            const response = await contactsAPI.getPublicProfile(attendee.contact_id);
            setProfileData(response.data);
        } catch (err) {
            console.error('Error fetching profile:', err);
            setProfileData(null);
        } finally {
            setProfileLoading(false);
        }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedAttendee(null);
        setProfileData(null);
    };

    // Handle opening meeting request drawer (for new meetings)
    const handleBookMeeting = (attendee, e) => {
        if (e) e.stopPropagation();
        setMeetingRequestContact({
            contact_id: attendee.contact_id,
            first_name: attendee.first_name,
            last_name: attendee.last_name,
            job_title: attendee.job_title,
            company_name: attendee.company_name,
            image_url: attendee.image_url,
            ic_contact_id: attendee.ic_contact_id
        });
        setMeetingRequestDrawerOpen(true);
    };

    const handleCloseMeetingRequestDrawer = () => {
        setMeetingRequestDrawerOpen(false);
        setMeetingRequestContact(null);
    };

    const handleMeetingRequestSuccess = (meetingId) => {
        console.log('Meeting request sent successfully. Meeting ID:', meetingId);
        setMeetingRequestDrawerOpen(false);
        setMeetingRequestContact(null);
        refreshMeetings();
    };

    // Handle opening meeting details drawer (for existing meetings)
    const handleViewMeeting = (meeting, companyName, e) => {
        if (e) e.stopPropagation();
        setSelectedMeetingId(meeting.meetingId);
        setSelectedMeetingCompanyName(companyName);
        setMeetingDetailsDrawerOpen(true);
    };

    const handleCloseMeetingDetailsDrawer = () => {
        setMeetingDetailsDrawerOpen(false);
        setSelectedMeetingId(null);
        setSelectedMeetingCompanyName(null);
    };

    const handleMeetingAction = () => {
        refreshMeetings();
    };

    // Get meeting status for an attendee using their ic_event_company_id
    const getAttendeeMeetingStatus = (attendee) => {
        if (!attendee?.ic_event_company_id) return null;
        const icEventCompanyId = parseInt(attendee.ic_event_company_id, 10);
        return getMeetingStatusForCompany(icEventCompanyId);
    };

    // Render meeting button based on status
    const renderMeetingButton = (attendee, isDrawer = false) => {
        const meetingInfo = getAttendeeMeetingStatus(attendee);
        
        const buttonClass = isDrawer ? 'attendee-drawer-meeting-btn' : 'attendee-meeting-btn';
        
        if (!meetingInfo) {
            // No existing meeting - show "Book a Meeting" button
            return (
                <button
                    className={buttonClass}
                    onClick={(e) => handleBookMeeting(attendee, e)}
                >
                    Book a Meeting
                </button>
            );
        }

        const { status, meeting } = meetingInfo;

        if (status === 'confirmed') {
            return (
                <button 
                    className={`${buttonClass} meeting-confirmed`}
                    onClick={(e) => handleViewMeeting(meeting, attendee.company_name, e)}
                >
                    <CheckCircle size={14} />
                    Meeting Confirmed
                </button>
            );
        }

        if (status === 'pending_sent') {
            return (
                <button 
                    className={`${buttonClass} meeting-pending`}
                    onClick={(e) => handleViewMeeting(meeting, attendee.company_name, e)}
                >
                    <Clock size={14} />
                    Meeting Pending
                </button>
            );
        }

        if (status === 'pending_received') {
            return (
                <button 
                    className={`${buttonClass} meeting-action-required`}
                    onClick={(e) => handleViewMeeting(meeting, attendee.company_name, e)}
                >
                    <Clock size={14} />
                    Meeting Action Required
                </button>
            );
        }

        if (status === 'cancelled') {
            return (
                <button 
                    className={`${buttonClass} meeting-cancelled`}
                    onClick={(e) => handleViewMeeting(meeting, attendee.company_name, e)}
                >
                    <X size={14} />
                    Meeting Cancelled
                </button>
            );
        }

        // Default: show Book a Meeting
        return (
            <button
                className={buttonClass}
                onClick={(e) => handleBookMeeting(attendee, e)}
            >
                Book a Meeting
            </button>
        );
    };

    // Get initials for profile avatar
    const getProfileInitials = () => {
        if (!profileData) return selectedAttendee ? `${selectedAttendee.first_name?.[0] || ''}${selectedAttendee.last_name?.[0] || ''}` : '?';
        const firstName = profileData.first_name || '';
        const lastName = profileData.last_name || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading attendees..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate('/login')} className="btn btn-primary">
                                Return to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar">
                    <div className="attendees-page">
                        <div className="attendees-header">
                            <div className="attendees-title-section">
                                <h1>Attendees ({filteredAttendees.length})</h1>
                                <p className="attendees-subtitle">
                                    Connect with attendees at {event.Name}
                                </p>
                            </div>
                        </div>

                        <div className="attendees-search-bar">
                            <div className="attendees-search-input-wrapper">
                                <Search size={18} className="attendees-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by Name, Company or Job title"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="attendees-search-input"
                                />
                            </div>
                            <div className="attendees-filter-wrapper" ref={filterRef}>
                                <button
                                    className={`attendees-filter-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
                                    onClick={() => {
                                        setFilterDropdownOpen(!filterDropdownOpen);
                                        if (!filterDropdownOpen) setFilterView('main');
                                    }}
                                >
                                    <Plus size={16} />
                                    Add filter
                                    {activeFilterCount > 0 && (
                                        <span className="filter-count">{activeFilterCount}</span>
                                    )}
                                </button>

                                {filterDropdownOpen && (
                                    <div className="attendees-filter-dropdown">
                                        {filterView === 'main' && (
                                            <>
                                                <div className="filter-menu-item" onClick={() => setFilterView('entity')}>
                                                    <span>Entity Type</span>
                                                    <div className="filter-menu-item-right">
                                                        {selectedEntityTypes.length > 0 && (
                                                            <span className="filter-count-badge">{selectedEntityTypes.length}</span>
                                                        )}
                                                        <ChevronRight size={16} />
                                                    </div>
                                                </div>
                                                {distinctAffinityGroups.length > 0 && (
                                                    <div className="filter-menu-item" onClick={() => setFilterView('affinity')}>
                                                        <span>Affinity Groups</span>
                                                        <div className="filter-menu-item-right">
                                                            {selectedAffinityGroups.length > 0 && (
                                                                <span className="filter-count-badge">{selectedAffinityGroups.length}</span>
                                                            )}
                                                            <ChevronRight size={16} />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {filterView === 'entity' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => setFilterView('main')}>
                                                    <ChevronLeft size={16} />
                                                    <span>Entity Type</span>
                                                </div>
                                                <div className="filter-options">
                                                    {distinctEntityTypes.map(entityType => (
                                                        <label key={entityType} className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedEntityTypes.includes(entityType)}
                                                                onChange={() => toggleFilter('entity', entityType)}
                                                            />
                                                            <span>{entityType}</span>
                                                        </label>
                                                    ))}
                                                    {distinctEntityTypes.length === 0 && (
                                                        <span className="filter-empty">No options available</span>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {filterView === 'affinity' && (
                                            <>
                                                <div className="filter-back-header" onClick={() => setFilterView('main')}>
                                                    <ChevronLeft size={16} />
                                                    <span>Affinity Groups</span>
                                                </div>
                                                <div className="filter-options">
                                                    {distinctAffinityGroups.map(group => (
                                                        <label key={group} className="filter-option">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedAffinityGroups.includes(group)}
                                                                onChange={() => toggleFilter('affinity', group)}
                                                            />
                                                            <span>{group}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {activeFilterCount > 0 && (
                                            <div className="filter-actions">
                                                <button className="filter-clear-btn" onClick={clearFilters}>
                                                    Clear all filters
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Active filter tags */}
                        {activeFilterCount > 0 && (
                            <div className="attendees-active-filters">
                                {selectedEntityTypes.map(type => (
                                    <span key={`type-${type}`} className="active-filter-tag">
                                        {type}
                                        <button onClick={() => toggleFilter('entity', type)}>×</button>
                                    </span>
                                ))}
                                {selectedAffinityGroups.map(group => (
                                    <span key={`affinity-${group}`} className="active-filter-tag">
                                        {group}
                                        <button onClick={() => toggleFilter('affinity', group)}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {attendees.length === 0 ? (
                            <div className="empty-state">
                                <h3>No Attendees Found</h3>
                                <p>There are no attendees registered for this event yet.</p>
                            </div>
                        ) : filteredAttendees.length === 0 ? (
                            <div className="empty-state">
                                <Search size={48} />
                                <h3>No Results Found</h3>
                                <p>No attendees match your search criteria. Try adjusting your search.</p>
                            </div>
                        ) : (
                            <div className="attendees-list">
                                {filteredAttendees.map(attendee => (
                                    <div 
                                        key={attendee.contact_id} 
                                        className="attendee-card"
                                        onClick={() => handleAttendeeClick(attendee)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="attendee-card-main">
                                            <div className="attendee-card-left">
                                                {attendee.image_url ? (
                                                    <img
                                                        src={attendee.image_url}
                                                        alt={`${attendee.first_name} ${attendee.last_name}`}
                                                        className="attendee-avatar"
                                                    />
                                                ) : (
                                                    <div className="attendee-avatar-placeholder">
                                                        {attendee.first_name?.[0]}{attendee.last_name?.[0]}
                                                    </div>
                                                )}
                                                <div className="attendee-info">
                                                    {attendee.entity_name && (
                                                        <span 
                                                            className="attendee-entity-badge"
                                                            style={attendee.entity_color ? {
                                                                '--entity-bg-color': `${attendee.entity_color}20`,
                                                                '--entity-border-color': `${attendee.entity_color}40`,
                                                                '--entity-text-color': attendee.entity_color
                                                            } : undefined}
                                                        >
                                                            {attendee.entity_name.toUpperCase()}
                                                        </span>
                                                    )}
                                                    <h3 className="attendee-name">
                                                        {attendee.first_name} {attendee.last_name}
                                                    </h3>
                                                    <p className="attendee-title">
                                                        {attendee.job_title && attendee.company_name
                                                            ? `${attendee.job_title} at ${attendee.company_name}`
                                                            : attendee.job_title || attendee.company_name || ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="attendee-card-right">
                                                {renderMeetingButton(attendee, false)}
                                            </div>
                                        </div>
                                        {attendee.entity_name === 'Philanthropist' && attendee.affinity_groups && attendee.affinity_groups.length > 0 && (
                                            <div className="attendee-affinity-panel">
                                                <button 
                                                    className={`affinity-toggle ${expandedAffinityGroups[attendee.contact_id] ? 'expanded' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedAffinityGroups(prev => ({
                                                            ...prev,
                                                            [attendee.contact_id]: !prev[attendee.contact_id]
                                                        }));
                                                    }}
                                                >
                                                    <span className="affinity-label">Affinity Groups ({attendee.affinity_groups.length})</span>
                                                    <ChevronDown size={14} className={`affinity-chevron ${expandedAffinityGroups[attendee.contact_id] ? 'rotated' : ''}`} />
                                                </button>
                                                {expandedAffinityGroups[attendee.contact_id] && (
                                                    <div className="affinity-tags">
                                                        {attendee.affinity_groups.map((group, idx) => (
                                                            <span key={idx} className="affinity-tag">{group}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Profile Drawer */}
                    {drawerOpen && (
                        <>
                            <div className="attendee-drawer-overlay" onClick={closeDrawer} />
                            <div className={`attendee-profile-drawer ${drawerOpen ? 'open' : ''}`}>
                                <div className="attendee-drawer-header">
                                    {selectedAttendee?.entity_name && (
                                        <span 
                                            className="attendee-drawer-entity-tag"
                                            style={selectedAttendee?.entity_color ? {
                                                '--entity-bg-color': `${selectedAttendee.entity_color}20`,
                                                '--entity-border-color': `${selectedAttendee.entity_color}40`,
                                                '--entity-text-color': selectedAttendee.entity_color
                                            } : undefined}
                                        >
                                            {selectedAttendee.entity_name.toUpperCase()}
                                        </span>
                                    )}
                                    <button className="attendee-drawer-close" onClick={closeDrawer} title="Close">
                                        <X size={20} />
                                    </button>
                                </div>

                                {profileLoading ? (
                                    <div className="attendee-drawer-loading">
                                        <LoadingSpinner message="Loading profile..." />
                                    </div>
                                ) : (
                                    <div className="attendee-drawer-content">
                                        {/* Profile Header */}
                                        <div className="attendee-drawer-profile-header">
                                            <div className="attendee-drawer-banner"></div>
                                            <div className="attendee-drawer-avatar-section">
                                                <div className="attendee-drawer-avatar">
                                                    {(profileData?.image_url || selectedAttendee?.image_url) ? (
                                                        <img 
                                                            src={profileData?.image_url || selectedAttendee?.image_url} 
                                                            alt={`${selectedAttendee?.first_name} ${selectedAttendee?.last_name}`} 
                                                        />
                                                    ) : (
                                                        <span>{getProfileInitials()}</span>
                                                    )}
                                                </div>
                                                <h2 className="attendee-drawer-name">
                                                    {profileData?.first_name || selectedAttendee?.first_name} {profileData?.last_name || selectedAttendee?.last_name}
                                                </h2>
                                                {(profileData?.job_title || selectedAttendee?.job_title) && (
                                                    <p className="attendee-drawer-title">
                                                        {profileData?.job_title || selectedAttendee?.job_title}
                                                    </p>
                                                )}
                                                {(profileData?.company_name || selectedAttendee?.company_name) && (
                                                    <p className="attendee-drawer-company">
                                                        {profileData?.company_name || selectedAttendee?.company_name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* About Section */}
                                        {profileData && (
                                            <div className="attendee-drawer-section">
                                                <h3 className="attendee-drawer-section-title">About</h3>
                                                
                                                {profileData.short_bio && (
                                                    <p className="attendee-drawer-bio">{profileData.short_bio}</p>
                                                )}

                                                <div className="attendee-drawer-info-grid">
                                                    {profileData.job_title && (
                                                        <div className="attendee-drawer-info-item">
                                                            <span className="info-label">Role</span>
                                                            <span className="info-value">{profileData.job_title}</span>
                                                        </div>
                                                    )}
                                                    {profileData.company_name && (
                                                        <div className="attendee-drawer-info-item">
                                                            <span className="info-label">Company</span>
                                                            <span className="info-value">{profileData.company_name}</span>
                                                        </div>
                                                    )}
                                                    {(profileData.city || profileData.state_province || profileData.country) && (
                                                        <div className="attendee-drawer-info-item">
                                                            <span className="info-label">Location</span>
                                                            <span className="info-value">
                                                                {[profileData.city, profileData.state_province, profileData.country]
                                                                    .filter(Boolean)
                                                                    .join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {profileData.timezone && (
                                                        <div className="attendee-drawer-info-item">
                                                            <span className="info-label">Time Zone</span>
                                                            <span className="info-value">{profileData.timezone}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Companies Section */}
                                        {profileData?.companies && profileData.companies.length > 0 && (
                                            <div className="attendee-drawer-section">
                                                <h3 className="attendee-drawer-section-title">Companies</h3>
                                                <div className="attendee-drawer-companies">
                                                    {profileData.companies.map((company) => (
                                                        <div key={company.CompanyId} className="attendee-drawer-company-card">
                                                            {company.LogoUrl && (
                                                                <img
                                                                    src={company.LogoUrl}
                                                                    alt={company.Name}
                                                                    className="company-logo"
                                                                />
                                                            )}
                                                            <div className="company-details">
                                                                <h4>{company.Name}</h4>
                                                                {(company.Location || company.City || company.StateProvince || company.Country) && (
                                                                    <p>
                                                                        {company.Location ||
                                                                            [company.City, company.StateProvince, company.Country]
                                                                                .filter(Boolean)
                                                                                .join(', ')}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Social Links Section */}
                                        {profileData && (profileData.linkedin_url || profileData.instagram) && (
                                            <div className="attendee-drawer-section">
                                                <h3 className="attendee-drawer-section-title">Social Links</h3>
                                                <div className="attendee-drawer-social">
                                                    {profileData.linkedin_url && (
                                                        <a
                                                            href={profileData.linkedin_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-link linkedin"
                                                        >
                                                            <Linkedin size={18} />
                                                            LinkedIn
                                                        </a>
                                                    )}
                                                    {profileData.instagram && (
                                                        <a
                                                            href={profileData.instagram.startsWith('http') ? profileData.instagram : `https://instagram.com/${profileData.instagram.replace(/^@/, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="social-link instagram"
                                                        >
                                                            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M10 0C7.28 0 6.94 0.01 5.88 0.06C2.73 0.22 0.22 2.73 0.06 5.88C0.01 6.94 0 7.28 0 10C0 12.72 0.01 13.06 0.06 14.12C0.22 17.27 2.73 19.78 5.88 19.94C6.94 19.99 7.28 20 10 20C12.72 20 13.06 19.99 14.12 19.94C17.27 19.78 19.78 17.27 19.94 14.12C19.99 13.06 20 12.72 20 10C20 7.28 19.99 6.94 19.94 5.88C19.78 2.73 17.27 0.22 14.12 0.06C13.06 0.01 12.72 0 10 0ZM10 1.8C12.67 1.8 12.99 1.81 14.04 1.86C16.78 1.99 18.01 3.24 18.14 5.96C18.19 7.01 18.2 7.33 18.2 10C18.2 12.67 18.19 12.99 18.14 14.04C18.01 16.76 16.78 18.01 14.04 18.14C12.99 18.19 12.67 18.2 10 18.2C7.33 18.2 7.01 18.19 5.96 18.14C3.22 18.01 1.99 16.76 1.86 14.04C1.81 12.99 1.8 12.67 1.8 10C1.8 7.33 1.81 7.01 1.86 5.96C1.99 3.24 3.22 1.99 5.96 1.86C7.01 1.81 7.33 1.8 10 1.8ZM10 4.86C7.16 4.86 4.86 7.16 4.86 10C4.86 12.84 7.16 15.14 10 15.14C12.84 15.14 15.14 12.84 15.14 10C15.14 7.16 12.84 4.86 10 4.86ZM10 13.33C8.16 13.33 6.67 11.84 6.67 10C6.67 8.16 8.16 6.67 10 6.67C11.84 6.67 13.33 8.16 13.33 10C13.33 11.84 11.84 13.33 10 13.33ZM15.34 3.46C14.67 3.46 14.13 4 14.13 4.67C14.13 5.34 14.67 5.88 15.34 5.88C16.01 5.88 16.54 5.34 16.54 4.67C16.54 4 16.01 3.46 15.34 3.46Z" />
                                                            </svg>
                                                            Instagram
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Footer with Meeting button */}
                                <div className="attendee-drawer-footer">
                                    {renderMeetingButton(selectedAttendee, true)}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Meeting Request Drawer */}
            <MeetingRequestDrawer
                isOpen={meetingRequestDrawerOpen}
                onClose={handleCloseMeetingRequestDrawer}
                contact={meetingRequestContact}
                eventId={event?.EventId}
                icEventId={event?.ic_event_id}
                onSuccess={handleMeetingRequestSuccess}
            />

            {/* Meeting Details Drawer */}
            <MeetingDetailsDrawer
                meetingId={selectedMeetingId}
                isOpen={meetingDetailsDrawerOpen}
                onClose={handleCloseMeetingDetailsDrawer}
                onMeetingCancelled={handleMeetingAction}
                counterpartyCompanyName={selectedMeetingCompanyName}
            />
        </>
    );
};

export default EventAttendees;
